/**
 * Shared, platform-agnostic login-collision decision.
 *
 * When a user authenticates with a provider, the SDK probes
 * `GET /v1/auth/identity-check?kind=login-method` for the existing account's
 * `signupMethod`. This function decides whether the current login may proceed.
 *
 * Previously this logic was duplicated verbatim in the web and React Native
 * `auth-success-handler.tsx`; it now lives here once. Both platforms import it.
 *
 * Rule: `email-OTP` and `google` are interchangeable, because Turnkey's hosted
 * Auth Proxy "Social linking" auto-links a Google login to an existing
 * email-OTP account with the SAME verified email AT THE SUB-ORG LEVEL — both
 * resolve to one sub-org, so wallets stay accessible. This requires no
 * server-side parent key.
 *
 * Backend normalizes OTP to `signupMethod = 'email'` on session create; Turnkey
 * and the client still speak `'otp'`. Treat those as the same method.
 *
 * `apple` and `x` are siloed UNLESS the account has already connected them
 * (a mismatched, unconnected-provider login is blocked, nudging the user back
 * to their original method):
 *   - Turnkey Social linking covers Google only — it does NOT auto-link Apple,
 *     so an Apple login on an email/Google account would land in a SEPARATE
 *     sub-org (no wallets) and fragment the account.
 *   - Consolidating providers server-side would need a parent-org API key with
 *     authority over every sub-org — an account-takeover primitive — so it is
 *     deliberately NOT done. The safe way to add Apple/X to an existing account
 *     is the in-session Connect flow (`POST /connect-provider` +
 *     `addOauthProvider`), where the client adds the provider to its OWN
 *     sub-org using the user's own session.
 *   - A CONNECTED provider is therefore a real authenticator on the account's
 *     own sub-org — logging in with it resolves to the same wallets, so the
 *     guard admits any method present in `connectedProviders` (returned by the
 *     same identity-check probe that supplies `signupMethod`).
 *
 * Apple "Hide My Email" relay logins surface `signupMethod = null` here (the
 * relay address matches no existing account) and are allowed through — the
 * duplicate-account case is accepted by design.
 */

const SOCIAL_LINKED_METHODS: ReadonlySet<string> = new Set(['otp', 'google']);

/** Backend stores OTP as `email`; client/Turnkey use `otp`. */
function canonicalizeAuthMethod(method: string): string {
  if (method === 'email') return 'otp';
  return method;
}

/**
 * @param signupMethod - the existing account's signup method (or null if no
 *   account matches the incoming identity).
 * @param currentMethod - the provider the user is logging in with now
 *   ('otp' | 'email' | 'google' | 'apple' | 'x' | 'wallet' | 'passkey').
 * @param connectedProviders - every provider linked to the matched account
 *   (`user_auth_providers` rows, from the same identity-check response).
 */
export function isLoginMethodAllowed(
  signupMethod: string | null,
  currentMethod: string,
  connectedProviders: readonly string[] = [],
): boolean {
  if (!signupMethod) return true;
  const signup = canonicalizeAuthMethod(signupMethod);
  const current = canonicalizeAuthMethod(currentMethod);
  if (signup === current) return true;
  if (connectedProviders.some((p) => canonicalizeAuthMethod(p) === current)) return true;
  return SOCIAL_LINKED_METHODS.has(signup) && SOCIAL_LINKED_METHODS.has(current);
}
